'use client'

import { m } from 'framer-motion'
import { CheckCircle2, Code2, Cpu, LineChart, MessageSquare, Palette, Sparkles } from 'lucide-react'

import AnimatedCounter from '@/components/AnimatedCounter'
import GlobalParticles from '@/components/canvas/GlobalParticles'

export default function AboutClient() {
  return (
    <div className="relative min-h-screen overflow-x-hidden pt-0 md:pt-20">
      <GlobalParticles />

      {/* BLOCK 1: MAIN BANNER (HERO) */}
      <section className="relative flex min-h-[90vh] items-center justify-center px-4">
        {/* Background Elements */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/4 left-1/2 h-[600px] w-[1000px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/20 via-purple-600/15 to-cyan-600/20 blur-[150px]" />
          <div
            className="absolute inset-0 bg-[url('/grid.svg')] [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))] bg-center"
            style={{
              opacity: 0.03,
              transform: 'perspective(1000px) rotateX(60deg) translateY(100px) scale(3)',
            }}
          />
        </div>

        <div className="relative z-10 mx-auto w-[92vw] max-w-[1400px] text-center sm:w-[90vw]">
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 backdrop-blur-sm sm:mb-10"
          >
            <Sparkles className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium tracking-widest text-gray-300 uppercase">
              ✦ О КОМПАНИИ
            </span>
          </m.div>

          <h1
            className="relative mb-8 leading-[0.95] font-bold tracking-tight"
            style={{ fontSize: 'clamp(3rem, 10vw, 8rem)' }}
          >
            Мы — <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Цифровой горизонт
            </span>
          </h1>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mx-auto mb-12 max-w-4xl text-base leading-relaxed font-light text-gray-400 sm:text-xl md:text-2xl"
          >
            Мы — команда из Майкопа, которая создаёт цифровые продукты для бизнеса по всей России.
            Сайты, ИИ-решения, маркетинг, дизайн — закрываем полный цикл без посредников и
            субподрядчиков.
            <br />
            <br />
            Наш подход прост: глубоко погружаемся в задачу, находим лучшее решение и доводим до
            результата. Не продаём часы работы — продаём рост вашего бизнеса.
          </m.p>
        </div>
      </section>

      {/* BLOCK 2: PHILOSOPHY */}
      <section className="relative py-24 sm:py-32">
        <div className="mx-auto grid w-[92vw] max-w-[1400px] items-center gap-16 sm:w-[90vw] lg:grid-cols-2">
          <div>
            <div className="mb-6 inline-flex w-fit items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2">
              <span className="text-sm font-medium tracking-widest text-purple-400 uppercase">
                НАША ФИЛОСОФИЯ
              </span>
            </div>
            <h2 className="mb-8 text-4xl leading-tight font-bold sm:text-5xl lg:text-6xl">
              Мы верим в силу <br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                цифровой трансформации
              </span>
            </h2>

            {/* Stats Grid */}
            <div className="mt-12 grid grid-cols-2 gap-8">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <AnimatedCounter
                  value={10}
                  suffix="+"
                  className="mb-2 block text-4xl font-bold text-white"
                />
                <span className="text-sm tracking-wider text-gray-400 uppercase">Лет на рынке</span>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <AnimatedCounter
                  value={20}
                  suffix="+"
                  className="mb-2 block text-4xl font-bold text-white"
                />
                <span className="text-sm tracking-wider text-gray-400 uppercase">Проектов</span>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <AnimatedCounter
                  value={20}
                  suffix="+"
                  className="mb-2 block text-4xl font-bold text-white"
                />
                <span className="text-sm tracking-wider text-gray-400 uppercase">Клиентов</span>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <AnimatedCounter
                  value={100}
                  suffix="%"
                  className="mb-2 block text-4xl font-bold text-white"
                />
                <span className="text-sm tracking-wider text-gray-400 uppercase">Отдача</span>
              </div>
            </div>
          </div>

          <div className="space-y-6 text-lg leading-relaxed font-light text-gray-400 sm:text-xl">
            <p>
              В мире, где технологии меняются каждый день, важно не просто успевать за трендами — а
              использовать их для роста бизнеса. Цифровой горизонт — это команда практиков:
              разработчиков, маркетологов и дизайнеров, которые знают, как превращать цифровые
              инструменты в прибыль.
            </p>
            <p>
              Мы не гонимся за количеством проектов. Каждый клиент для нас — партнёр, в успехе
              которого мы заинтересованы лично. Поэтому работаем на результат, а не на красивые
              отчёты.
            </p>
          </div>
        </div>
      </section>

      {/* BLOCK 3: TEAM */}
      <section className="relative bg-white/[0.02] py-24 sm:py-32">
        <div className="mx-auto w-[92vw] max-w-[1400px] sm:w-[90vw]">
          <h2 className="mb-16 text-center text-4xl font-bold sm:text-5xl">
            Команда профессионалов
          </h2>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Member 1: Oleg */}
            <div className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-blue-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="relative z-10">
                <h3 className="mb-1 text-2xl font-bold text-white">Олег Носиков</h3>
                <p className="mb-6 text-sm tracking-wider text-blue-400 uppercase">
                  Основатель и руководитель
                </p>
                <blockquote className="relative font-light text-gray-400 italic">
                  <span className="absolute -top-4 -left-2 text-4xl text-blue-500/20">&quot;</span>
                  Мы создаём не просто сайты и рекламу — мы строим системы, которые приносят деньги.
                  Каждый проект для нас — это вызов и возможность доказать, что цифровые решения
                  работают.
                </blockquote>
              </div>
            </div>

            {/* Member 2: Asker */}
            <div className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-purple-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="relative z-10">
                <h3 className="mb-1 text-2xl font-bold text-white">Аскер Симбулетов</h3>
                <p className="mb-6 text-sm tracking-wider text-purple-400 uppercase">Разработчик</p>
                <p className="font-light text-gray-400 transition-colors group-hover:text-gray-300">
                  Пишет код, который работает быстро и стабильно. Фронтенд, бэкенд, интеграции —
                  превращает дизайн в живой продукт.
                </p>
              </div>
            </div>

            {/* Member 3: Julia */}
            <div className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-pink-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-pink-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="relative z-10">
                <h3 className="mb-1 text-2xl font-bold text-white">Юлия Носикова</h3>
                <p className="mb-6 text-sm tracking-wider text-pink-400 uppercase">
                  СММ-специалист
                </p>
                <p className="font-light text-gray-400 transition-colors group-hover:text-gray-300">
                  Строит присутствие брендов в соцсетях. Стратегия, контент, комьюнити — превращает
                  подписчиков в лояльных клиентов.
                </p>
              </div>
            </div>

            {/* Member 4: Ksenia */}
            <div className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-orange-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="relative z-10">
                <h3 className="mb-1 text-2xl font-bold text-white">Ксения Ендовицкая</h3>
                <p className="mb-6 text-sm tracking-wider text-orange-400 uppercase">Дизайнер</p>
                <p className="font-light text-gray-400 transition-colors group-hover:text-gray-300">
                  Создаёт визуал, который продаёт. UI/UX, фирменный стиль, креативы — каждый пиксель
                  работает на результат.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 4: COMPETENCIES */}
      <section className="relative py-24 sm:py-32">
        <div className="mx-auto w-[92vw] max-w-[1400px] sm:w-[90vw]">
          <div className="mb-20 text-center">
            <h2 className="mb-6 text-4xl font-bold sm:text-5xl lg:text-6xl">
              Ключевые{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                компетенции
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-xl text-gray-400">
              Закрываем полный цикл цифровых задач. Одна команда — все решения под ключ.
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6">
            {/* 01 Development */}
            <div className="group relative rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-blue-500/30 sm:p-10">
              <div className="relative z-10 flex flex-col justify-between gap-6 md:flex-row md:items-center">
                <div className="flex items-start gap-6">
                  <span className="text-4xl font-bold text-white/10 transition-colors group-hover:text-blue-500/50">
                    01
                  </span>
                  <div>
                    <h3 className="mb-2 text-2xl font-bold text-white transition-colors group-hover:text-blue-400">
                      Разработка
                    </h3>
                    <p className="text-lg text-gray-400">
                      Сайты, приложения, веб-сервисы, интеграции
                    </p>
                  </div>
                </div>
                <div className="flex w-fit items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-4 py-2 text-xs font-bold tracking-widest text-blue-400 uppercase">
                  <Code2 className="h-4 w-4" /> Технологии
                </div>
              </div>
            </div>

            {/* 02 ИИ */}
            <div className="group relative rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-purple-500/30 sm:p-10">
              <div className="relative z-10 flex flex-col justify-between gap-6 md:flex-row md:items-center">
                <div className="flex items-start gap-6">
                  <span className="text-4xl font-bold text-white/10 transition-colors group-hover:text-purple-500/50">
                    02
                  </span>
                  <div>
                    <h3 className="mb-2 text-2xl font-bold text-white transition-colors group-hover:text-purple-400">
                      ИИ-автоматизация
                    </h3>
                    <p className="text-lg text-gray-400">
                      Чат-боты, голосовые ассистенты, базы знаний, автоворонки
                    </p>
                  </div>
                </div>
                <div className="flex w-fit items-center gap-2 rounded-full border border-purple-500/20 bg-purple-500/10 px-4 py-2 text-xs font-bold tracking-widest text-purple-400 uppercase">
                  <Cpu className="h-4 w-4" /> ИИ
                </div>
              </div>
            </div>

            {/* 03 Design */}
            <div className="group relative rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-orange-500/30 sm:p-10">
              <div className="relative z-10 flex flex-col justify-between gap-6 md:flex-row md:items-center">
                <div className="flex items-start gap-6">
                  <span className="text-4xl font-bold text-white/10 transition-colors group-hover:text-orange-500/50">
                    03
                  </span>
                  <div>
                    <h3 className="mb-2 text-2xl font-bold text-white transition-colors group-hover:text-orange-400">
                      Дизайн
                    </h3>
                    <p className="text-lg text-gray-400">
                      UI/UX, веб-дизайн, брендинг, презентации
                    </p>
                  </div>
                </div>
                <div className="flex w-fit items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/10 px-4 py-2 text-xs font-bold tracking-widest text-orange-400 uppercase">
                  <Palette className="h-4 w-4" /> Креатив
                </div>
              </div>
            </div>

            {/* 04 Marketing */}
            <div className="group relative rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-green-500/30 sm:p-10">
              <div className="relative z-10 flex flex-col justify-between gap-6 md:flex-row md:items-center">
                <div className="flex items-start gap-6">
                  <span className="text-4xl font-bold text-white/10 transition-colors group-hover:text-green-500/50">
                    04
                  </span>
                  <div>
                    <h3 className="mb-2 text-2xl font-bold text-white transition-colors group-hover:text-green-400">
                      Маркетинг
                    </h3>
                    <p className="text-lg text-gray-400">
                      Контекстная реклама, СЕО, аналитика, воронки продаж
                    </p>
                  </div>
                </div>
                <div className="flex w-fit items-center gap-2 rounded-full border border-green-500/20 bg-green-500/10 px-4 py-2 text-xs font-bold tracking-widest text-green-400 uppercase">
                  <LineChart className="h-4 w-4" /> Рост
                </div>
              </div>
            </div>

            {/* 05 SMM */}
            <div className="group relative rounded-[32px] border border-white/10 bg-[#0c1220] p-8 transition-all duration-300 hover:border-pink-500/30 sm:p-10">
              <div className="relative z-10 flex flex-col justify-between gap-6 md:flex-row md:items-center">
                <div className="flex items-start gap-6">
                  <span className="text-4xl font-bold text-white/10 transition-colors group-hover:text-pink-500/50">
                    05
                  </span>
                  <div>
                    <h3 className="mb-2 text-2xl font-bold text-white transition-colors group-hover:text-pink-400">
                      СММ
                    </h3>
                    <p className="text-lg text-gray-400">
                      Стратегия, контент, таргет, комьюнити-менеджмент
                    </p>
                  </div>
                </div>
                <div className="flex w-fit items-center gap-2 rounded-full border border-pink-500/20 bg-pink-500/10 px-4 py-2 text-xs font-bold tracking-widest text-pink-400 uppercase">
                  <MessageSquare className="h-4 w-4" /> Соцсети
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BONUS BLOCK: WHY US */}
      <section className="relative bg-white/[0.02] py-24 sm:py-32">
        <div className="mx-auto w-[92vw] max-w-[1400px] sm:w-[90vw]">
          <h2 className="mb-16 text-center text-4xl font-bold sm:text-5xl">Почему выбирают нас</h2>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Reason 1 */}
            <div className="rounded-[32px] border border-white/10 bg-[#0c1220] p-8">
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/20">
                <CheckCircle2 className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white">Локальная команда</h3>
              <p className="leading-relaxed text-gray-400">
                Мы в Майкопе — всегда на связи, можем встретиться лично. Но работаем с клиентами по
                всей России.
              </p>
            </div>

            {/* Reason 2 */}
            <div className="rounded-[32px] border border-white/10 bg-[#0c1220] p-8">
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/20">
                <CheckCircle2 className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white">Полный цикл</h3>
              <p className="leading-relaxed text-gray-400">
                Не нужно искать отдельно разработчиков, дизайнеров и маркетологов. Всё в одних руках
                — быстрее, дешевле, качественнее.
              </p>
            </div>

            {/* Reason 3 */}
            <div className="rounded-[32px] border border-white/10 bg-[#0c1220] p-8">
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
                <CheckCircle2 className="h-6 w-6 text-green-400" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white">Фокус на результат</h3>
              <p className="leading-relaxed text-gray-400">
                Не продаём часы и процессы. Продаём рост: заявки, продажи, автоматизацию. То, что
                можно измерить.
              </p>
            </div>

            {/* Reason 4 */}
            <div className="rounded-[32px] border border-white/10 bg-[#0c1220] p-8">
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/20">
                <CheckCircle2 className="h-6 w-6 text-orange-400" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white">Честные цены</h3>
              <p className="leading-relaxed text-gray-400">
                Работаем без накруток агентств из Москвы. Премиальное качество по адекватной
                стоимости.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
